/**
 * Domain API — the replacement for `supabase.from(...)` / storage / functions.
 * Import { Expenses, Profiles, Users, Receipts, getMe } from '@/integrations/api'.
 */
import { api } from './client';
export { ApiError } from './client';
import type {
  AppRole,
  CreateExpenseResult,
  DirectoryEntry,
  Expense,
  ExpenseInput,
  ManagedUser,
  Me,
  Profile,
  ReceiptOcrSuggestion,
} from './types';

export * from './types';

export const getMe = () => api.get('/api/me') as Promise<Me>;

export const Expenses = {
  /** The signed-in employee's own expenses. */
  listMine: () => api.get('/api/expenses/mine') as Promise<Expense[]>,
  /** Reviewer view: all expenses, optionally for one employee. */
  listAll: (userId?: string) =>
    api.get('/api/expenses' + (userId ? `?user_id=${encodeURIComponent(userId)}` : '')) as Promise<Expense[]>,
  create: (input: ExpenseInput) => api.post('/api/expenses', input) as Promise<CreateExpenseResult>,
  update: (id: string, input: ExpenseInput) => api.patch(`/api/expenses/${id}`, input) as Promise<Expense>,
  changeStatus: (id: string, status: Expense['status']) =>
    api.patch(`/api/expenses/${id}/status`, { status }) as Promise<Expense>,
  reject: (id: string, rejectionReason: string) =>
    api.patch(`/api/expenses/${id}/reject`, { rejection_reason: rejectionReason }) as Promise<Expense>,
  markRecordedAsPaid: (userId: string) =>
    api.post(`/api/expenses/mark-paid?user_id=${encodeURIComponent(userId)}`) as Promise<{ updated: number }>,
  remove: (id: string) => api.del(`/api/expenses/${id}`) as Promise<null>,
  /**
   * Reviewer-only: ZIP up these expenses' receipts as their original files and return a
   * downloadable blob URL plus the backend-suggested filename (initials + receipt-date
   * range + total). Expenses without a receipt are skipped server-side.
   */
  downloadReceiptsBundle: (ids: string[]) =>
    api.download(`/api/expenses/receipts/bundle?ids=${ids.map(encodeURIComponent).join(',')}`),
};

export const Profiles = {
  listAll: () => api.get('/api/profiles') as Promise<Profile[]>,
  get: (userId: string) => api.get(`/api/profiles/${encodeURIComponent(userId)}`) as Promise<Profile>,
  /** Lightweight employee list (initials + name) for the participants picker. */
  directory: () => api.get('/api/profiles/directory') as Promise<DirectoryEntry[]>,
};

export const Users = {
  list: () => api.get('/api/users') as Promise<ManagedUser[]>,
  create: (input: { email: string; display_name?: string; role: AppRole }) =>
    api.post('/api/users', input) as Promise<ManagedUser>,
  remove: (id: string) => api.del(`/api/users/${encodeURIComponent(id)}`) as Promise<null>,
  updateRole: (id: string, role: AppRole) =>
    api.patch(`/api/users/${encodeURIComponent(id)}/role`, { role }) as Promise<null>,
};

export const Receipts = {
  /**
   * Upload a receipt image; returns its storage key as { path }. If the same user has
   * already uploaded the exact same file, the backend rejects it with 409 (ApiError.status)
   * — one physical file may back only one expense, so the caller must pick a different file.
   */
  upload: (file: File) =>
    api.upload('/api/receipts', file) as Promise<{ path: string; url: string }>,
  /**
   * OCR-suggest an amount/date from a receipt photo — a hint the caller may prefill, never
   * an authoritative value. `*_found` is false whenever the OCR pipeline wasn't confident
   * enough to guess (blurry photo, ambiguous totals/dates, etc.); `amount`/`receipt_date`
   * are then null. Nothing is persisted by this call — it's a stateless scan.
   */
  extractFields: (file: File) =>
    api.upload('/api/receipts/extract-fields', file) as Promise<ReceiptOcrSuggestion>,
  /**
   * Get a displayable blob URL for a stored receipt. Accepts either a bare storage key
   * ("<userId>/<file>") or a full "/api/receipts/..." path. Revoke the URL when done.
   */
  objectUrl: (keyOrUrl: string) => {
    const path = keyOrUrl.startsWith('/api/receipts/')
      ? keyOrUrl
      : `/api/receipts/${keyOrUrl.replace(/^\/+/, '')}`;
    return api.objectUrl(path);
  },
};
